import { useEffect, useRef, useState } from "react";

import Image from "next/image";
import { useRouter } from "next/router";

import Button from "@/components/Button";
import Icon from "@/components/Icon";
import { QRScannerCamera } from "@/components/QRScanner";
import SpinnerLoading from "@/components/SpinnerLoading";
import useTranslation from "@/hooks/useTranslation";
import CameraLensSVG from "@/images/camera-lens.svg";
import EmojiSadSVG from "@/images/emoji-sad.svg";
import QRScannerHole from "@/images/scanner-hole.svg";
import QRScannerOutline from "@/images/scanner-outline.svg";
import IQRCodeResult from "@/interfaces/IQRCodeResult";
import { useErrorModal } from "@/modals/ErrorModal";
import { GetCompany, GetQRCodeData } from "@/services/api/Company";
import { IS_PRODUCTION } from "@/utils/GetSessionMode";
import { InstallPrompt } from "@/utils/InstallPrompt";
import { IsReactNative, LoadingFinished } from "@/utils/ReactNativeUtils";

import {
  QRScannerContainer,
  QRScannerDownloadButton,
  QRScannerInput,
  QRScannerInputContainer,
  QRScannerLaser,
  QRScannerLogo,
  QRScannerMessageContainer,
  QRScannerOverlayContainer,
  QRScannerPermissionSubtitle,
  QRScannerPermissionTitle,
  QRScannerResultLoading,
  QRScannerStepButton,
  QRScannerText,
  QRScannerVideo,
} from "./styles";

interface Props {
  isTablet?: boolean;
  useCommand?: boolean;
  onScan: (result: IQRCodeResult, data: string) => Promise<void> | void;
}

export default function QRScannerPage({ isTablet, useCommand, onScan }: Props) {
  const scannerRef = useRef<HTMLDivElement>();
  const { replace, pathname } = useRouter();
  const [cameraError, setCameraError] = useState<string>();
  const [mustDownloadApp, setMustDownloadApp] = useState(false);
  const [queryQrIsLoading, setQueryQrIsLoading] = useState(true);
  const [scannerIsLoading, setScannerIsLoading] = useState(true);
  const [codeIsLoading, setCodeIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const { show: showErrorModal } = useErrorModal();
  const {
    translation: { qrScanner: translation },
  } = useTranslation();

  async function handleScan(qrCode: string) {
    if (!onScan) return;
    if (!qrCode.startsWith("http") && qrCode.length !== 32) return;

    if (
      qrCode.startsWith(window.location.origin) ||
      (process.env.NODE_ENV === "development" && qrCode.startsWith("http"))
    ) {
      const urlQrCode = new URL(qrCode).searchParams.get("qr");
      if (urlQrCode) qrCode = urlQrCode;
    }

    // https://xxxxx.gestormetre.com/Detail/?q=123
    if (/^https:\/\/.+\.gestormetre\.com\/Detail\/\?q=\d+$/.test(qrCode)) {
      const commandNumber = parseInt(new URL(qrCode).searchParams.get("q"), 10);
      if (!(commandNumber > 0)) return;

      if (useCommand === false) {
        showErrorModal(translation.readQrCodeToAccess);
        setInputValue("");
        return;
      }

      await onScan(
        {
          is_tablet: false,
          id_company: null,
          table_number: null,
          command_number: commandNumber,
        },
        ""
      );
      return;
    }

    if (!/^[a-fA-F0-9]{32}$/.test(qrCode)) return;

    setCodeIsLoading(true);

    try {
      const qrCodeData = await GetQRCodeData(qrCode);

      if (useCommand && !qrCodeData.command_number) {
        showErrorModal(translation.readCommandQrCodeToAccess);
        setInputValue("");
      } else if (useCommand === false && !!qrCodeData.command_number) {
        showErrorModal(translation.readQrCodeToAccess);
        setInputValue("");
      } else if (qrCodeData.is_tablet && !IsReactNative() && IS_PRODUCTION) {
        setMustDownloadApp(true);
      } else if (qrCodeData.is_tablet) {
        const company = await GetCompany(qrCodeData.id_company);

        if (!company.license.tablet_slots) {
          showErrorModal(translation.cannotAccessTablet);
        } else {
          await onScan(qrCodeData, qrCode);
        }
      } else {
        await onScan(qrCodeData, qrCode);
      }

      setCodeIsLoading(false);
    } catch (error) {
      showErrorModal(error);
      setCodeIsLoading(false);
      setQueryQrIsLoading(false);
    }
  }

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);

    if (query.has("qr")) {
      replace(pathname, null, { shallow: true });
      setInputValue(query.get("qr"));
      handleScan(query.get("qr"));
    } else {
      setQueryQrIsLoading(false);
    }

    scannerRef.current.getAnimations().forEach((animation) => {
      animation.currentTime = 0;
    });
  }, []);

  if (mustDownloadApp)
    return (
      <QRScannerMessageContainer>
        <Image
          src="/images/icon-192.png"
          alt="Logo App Garçom Digital"
          width={100}
          height={100}
        />
        <QRScannerPermissionTitle>App Garçom Digital</QRScannerPermissionTitle>
        <QRScannerPermissionSubtitle>
          {translation.needInstallApp}
        </QRScannerPermissionSubtitle>
        <QRScannerDownloadButton onClick={() => InstallPrompt()}>
          {translation.installApp}
          <Icon name="download-fill" />
        </QRScannerDownloadButton>
        <QRScannerStepButton onClick={() => setMustDownloadApp(false)}>
          {translation.scanAnotherCode}
          <Icon name="arrow-drop-right-line" />
        </QRScannerStepButton>
      </QRScannerMessageContainer>
    );

  if (cameraError === "NotAllowedError")
    return (
      <QRScannerMessageContainer>
        <CameraLensSVG />
        <QRScannerPermissionTitle>
          {translation.requiredCameraPermission}
        </QRScannerPermissionTitle>
        <QRScannerPermissionSubtitle>
          {translation.requireCameraPermission}
        </QRScannerPermissionSubtitle>
        <QRScannerStepButton onClick={() => setCameraError("NotFoundError")}>
          {translation.manualQrCode}
          <Icon name="arrow-drop-right-line" />
        </QRScannerStepButton>
      </QRScannerMessageContainer>
    );

  if (cameraError)
    return (
      <QRScannerMessageContainer>
        <EmojiSadSVG />
        <QRScannerPermissionTitle>
          {translation.cannotAccessCamera}
        </QRScannerPermissionTitle>
        <QRScannerPermissionSubtitle>
          {useCommand ? translation.enterCommandQrCode : translation.enterQrCode}
        </QRScannerPermissionSubtitle>
        <QRScannerInputContainer>
          <QRScannerInput
            placeholder="00000000000000000000000000000000"
            value={inputValue}
            onPaste={(event) => {
              const text = event.clipboardData
                .getData("text")
                .replace(/[^a-fA-F0-9]+/g, "");

              if (/^[a-fA-F0-9]{32}$/.test(text)) {
                event.preventDefault();
                setInputValue(text);
              }
            }}
            onChange={(event) => {
              setInputValue(event.target.value.replace(/[^a-fA-F0-9]+/g, ""));
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleScan(inputValue);
            }}
            minLength={32}
            maxLength={32}
          />
          <Button
            onClick={() => handleScan(inputValue)}
            aria-label={translation.search}
            disabled={inputValue?.length !== 32}
          >
            <Icon name="arrow-drop-right-line" />
          </Button>
        </QRScannerInputContainer>
        {codeIsLoading && (
          <QRScannerResultLoading opaque>
            <SpinnerLoading size={25} />
          </QRScannerResultLoading>
        )}
      </QRScannerMessageContainer>
    );

  return (
    <QRScannerContainer>
      {!queryQrIsLoading && (
        <QRScannerVideo
          preferredCamera={isTablet ? QRScannerCamera.User : QRScannerCamera.Environment}
          onScan={handleScan}
          onPlay={() => {
            setScannerIsLoading(false);
            LoadingFinished();
          }}
          onError={(error) => {
            setCameraError(error.name);
            setScannerIsLoading(false);
            LoadingFinished();
            throw error;
          }}
          paused={codeIsLoading}
        />
      )}
      <QRScannerOverlayContainer>
        <QRScannerHole />
        <QRScannerOutline className="outline" />
        <QRScannerLaser ref={scannerRef}>
          <div className="line"></div>
          <div className="shadow"></div>
        </QRScannerLaser>
      </QRScannerOverlayContainer>
      <QRScannerText>
        {useCommand
          ? translation.readCommandQrCodeToAccess
          : translation.readQrCodeToAccess}
      </QRScannerText>
      <QRScannerLogo href="https://www.poppedidos.com.br/" target="_blank">
        <img src="/images/logo-full.svg" width={104} height={40} alt="Logo" />
      </QRScannerLogo>
      {(scannerIsLoading || codeIsLoading) && (
        <QRScannerResultLoading>
          <SpinnerLoading size={25} />
        </QRScannerResultLoading>
      )}
    </QRScannerContainer>
  );
}
